/* eslint-disable jsx-a11y/anchor-is-valid */

import React from 'react';
import MediaBreakpoints from 'Utils/MediaBreakpoints';
import { connect } from 'react-redux';
import classnames from 'classnames';
import {
  renderItemQty,
  isItem,
} from 'HelperFunctions/rentals';
import CurrencyLabel from 'Utils/CurrencyLabel';
import { Items, RemoveCircle } from 'Utils/SvgIcons';
import Imgix from 'react-imgix';
import { localeToCurrencySymbol } from 'Constants/currencyConstants';
import CurrencyFormatter from 'HelperFunctions/CurrencyFormatter';
import { formatSmartPrincingLabel } from 'HelperFunctions/smartPricing';
import { sanitizedData } from 'HelperFunctions/sanitizeData';
import ItemNotesModal from './ItemNotesModal';
import {
  DISCOUNT_DISPLAY_MODES,
  DISCOUNT_DISPLAY_DEFAULT,
} from 'Constants/discountDisplay';

class RentalItem extends React.Component {
  renderItemPeriodPrice = () => {
    const {
      rentalItem,
      index,
      sectionIndex,
      removeDuplicatedItem,
      editing,
      parentConnectionType,
    } = this.props;
    const item = rentalItem;

    if (parentConnectionType === 'duplicate') {
      return (
        <td className='medSm right normalText'>
          Duplicate
          {editing && (
            <a
              className='btnLink delete'
              onClick={() => removeDuplicatedItem(sectionIndex, index)}
              style={{ paddingLeft: '5px' }}
            >
              <RemoveCircle />
            </a>
          )}
        </td>
      );
    }

    return (
      <td className='medSm right normalText'>
        {isItem(item) ? (
          <div>{item.periodPrice}</div>
        ) : (
          <div>
            <CurrencyLabel
              value={item.selectedPrice ? item.selectedPrice / item.quantity : 0}
            />
          </div>
        )}
      </td>
    );
  };
  // Value-only content helpers (return inner JSX, not a <td>) so the desktop
  // cells and the mobile labeled-lines share the EXACT same logic.
  rateContent = () => {
    const { rentalItem } = this.props;
    const item = rentalItem;
    // periodPrice already encodes custom "Set Total Cost" formatting ("$X / N Qty").
    return isItem(item) ? (
      item.periodPrice
    ) : (
      <CurrencyLabel
        value={item.selectedPrice ? item.selectedPrice / item.quantity : 0}
      />
    );
  };

  feeContent = () => {
    const { rentalItem } = this.props;
    const totalFees = parseFloat(rentalItem.totalFees) || 0;
    return totalFees > 0 ? <CurrencyLabel value={totalFees} /> : null;
  };

  discountContent = () => {
    const { rentalItem } = this.props;
    const totalDiscounts = parseFloat(rentalItem.totalDiscounts) || 0;
    return totalDiscounts > 0 ? (
      <CurrencyLabel value={-Math.abs(totalDiscounts)} />
    ) : null;
  };

  // The Subtotal always reflects the discounted (real) value, regardless of
  // discount-display mode (matching the admin tables). summary_only flags it
  // with an asterisk; subtotal_strike shows a strikethrough pre-discount value
  // WITH fees (preDiscountSubtotal) beside the final value.
  subtotalContent = () => {
    const {
      rentalItem,
      storefrontShopSetting,
      approvalStatus,
      sectionMembership,
      discountDisplay,
    } = this.props;
    const item = rentalItem;

    if (
      approvalStatus !== 'approved' &&
      !storefrontShopSetting.shopShowItemPricing
    ) {
      return null;
    }
    if (sectionMembership.parentConnectionType === 'duplicate') {
      return 'N/A';
    }

    const mode = discountDisplay || DISCOUNT_DISPLAY_DEFAULT;
    const priceBeforeDiscount = parseFloat(item.selectedPriceBeforeDiscount);
    const selectedPrice = parseFloat(item.selectedPrice);
    const discounted = !isNaN(selectedPrice) ? selectedPrice : 0;
    const preDiscountSubtotal = parseFloat(item.preDiscountSubtotal);
    const struck = !isNaN(preDiscountSubtotal)
      ? preDiscountSubtotal
      : !isNaN(priceBeforeDiscount)
        ? priceBeforeDiscount
        : discounted;
    const rowHasDiscount =
      isItem(item) &&
      Boolean(item.rentalItemDiscountRelationships?.length) &&
      !isNaN(priceBeforeDiscount) &&
      !isNaN(selectedPrice) &&
      priceBeforeDiscount !== selectedPrice;

    if (mode === DISCOUNT_DISPLAY_MODES.SUMMARY_ONLY && rowHasDiscount) {
      return (
        <span>
          <CurrencyLabel value={discounted} />
          {' *'}
        </span>
      );
    }
    if (mode === DISCOUNT_DISPLAY_MODES.SUBTOTAL_STRIKE && rowHasDiscount) {
      return (
        <span>
          <span className='strikeThrough'>
            <CurrencyLabel value={struck} />
          </span>{' '}
          <CurrencyLabel value={discounted} />
        </span>
      );
    }
    return item.selectedPrice ? <CurrencyLabel value={discounted} /> : null;
  };

  renderItemSelectedPrice = () => {
    return <td className='medSm right'>{this.subtotalContent()}</td>;
  };

  renderFeeCell = () => {
    const { showFeesCol } = this.props;
    if (!showFeesCol) return null;
    return <td className='medSm right'>{this.feeContent()}</td>;
  };

  renderDiscountCell = () => {
    const { showDiscountsCol } = this.props;
    if (!showDiscountsCol) return null;
    return <td className='medSm right'>{this.discountContent()}</td>;
  };

  render() {
    const {
      rentalItem,
      showItemImages,
      showItemNotes,
      showUnitPricingToCustomers,
      tileView,
      approvalStatus,
      storefrontShopSetting,
      sectionMembership,
      showFeesCol,
      showDiscountsCol,
    } = this.props;
    const product = rentalItem.product || rentalItem.addOn;
    const mobileImageUrl =
      product && product.pictures[0]
        ? product.pictures[0].imgixUrl || product.pictures[0].thumbnailUrl
        : null;
    const hasMobileImage = Boolean(tileView && showItemImages && mobileImageUrl);
    const itemNotesVisibleToCustomer = rentalItem.itemNotes
      ? rentalItem.itemNotes.filter(
          (itemNote) => itemNote.shouldIncludeNotesInRentals
        )
      : [];
    // Mobile line values — reuse the same content helpers as the desktop cells,
    // with the same section-level gates (showFeesCol/showDiscountsCol/pricing).
    const pricingVisible =
      approvalStatus === 'approved' &&
      showUnitPricingToCustomers &&
      storefrontShopSetting.shopShowItemPricing;
    const rateLine = pricingVisible ? this.rateContent() : null;
    const feeLine = showFeesCol ? this.feeContent() : null;
    const discountLine = showDiscountsCol ? this.discountContent() : null;
    const subtotalLine = this.subtotalContent();
    return (
      <MediaBreakpoints
        desktop={
          <tbody className='itemContent'>
            <tr
              className={classnames({
                bottom: showItemNotes && itemNotesVisibleToCustomer.length > 0,
              })}
            >
              {tileView && showItemImages && (
                <td rowSpan='2' className='image'>
                  <figure>
                    {product &&
                    product.pictures[0] &&
                    product.pictures[0].thumbnailUrl ? (
                      <Imgix
                        src={product.pictures[0].thumbnailUrl}
                        alt={rentalItem.name}
                        width={95}
                      />
                    ) : (
                      <Items />
                    )}
                  </figure>
                </td>
              )}
              <td className='normalText'>
                {rentalItem.name}
                {showItemNotes &&
                  itemNotesVisibleToCustomer?.map((itemNote) => (
                    <span
                      className='caption editedText'
                      style={{ display: 'block' }}
                      dangerouslySetInnerHTML={sanitizedData(itemNote.message)}
                    />
                  ))}
              </td>
              <td
                className={classnames({
                  right: true,
                  alert: rentalItem.hasShortage,
                })}
              >
                {renderItemQty(rentalItem, sectionMembership.quantity)}
              </td>
              {approvalStatus === 'approved' &&
                showUnitPricingToCustomers &&
                storefrontShopSetting.shopShowItemPricing &&
                this.renderItemPeriodPrice()}
              {this.renderFeeCell()}
              {this.renderDiscountCell()}
              {this.renderItemSelectedPrice()}
            </tr>
          </tbody>
        }
        mobile={
          <tbody className='mobileItemContent'>
            {/* Mobile: only render an image when one exists, and show it
                full-width (no reserved space / placeholder when missing). */}
            {hasMobileImage && (
              <tr>
                <td>
                  <figure className='mobileFullImage'>
                    <Imgix
                      src={mobileImageUrl}
                      alt={rentalItem.name}
                      width={600}
                    />
                  </figure>
                </td>
              </tr>
            )}
            <tr
              className={classnames({
                noBorder: hasMobileImage,
              })}
            >
              <td className={classnames({ alert: rentalItem.hasShortage })}>
                {rentalItem.name}
                {/* Stacked labeled lines instead of columns on mobile; same
                    per-mode logic as the desktop cells. */}
                <div className='caption lineDetails'>
                  <div>
                    <strong>Quantity</strong>{' '}
                    {renderItemQty(rentalItem, sectionMembership.quantity)}
                  </div>
                  {rateLine != null && (
                    <div>
                      <strong>Rate</strong> {rateLine}
                    </div>
                  )}
                  {feeLine != null && (
                    <div>
                      <strong>Fees</strong> {feeLine}
                    </div>
                  )}
                  {discountLine != null && (
                    <div>
                      <strong>Discounts</strong> {discountLine}
                    </div>
                  )}
                  {subtotalLine != null && (
                    <div>
                      <strong>Subtotal</strong> {subtotalLine}
                    </div>
                  )}
                </div>
                {/* Rich-text notes live in a modal on mobile (TD1-10310). */}
                {showItemNotes && (
                  <ItemNotesModal
                    itemName={rentalItem.name}
                    itemNotes={itemNotesVisibleToCustomer}
                  />
                )}
              </td>
            </tr>
          </tbody>
        }
      />
    );
  }
}
const mapStateToProps = (state) => {
  const { parsedStorefrontSettings, location } = state.locations;
  const storefrontShopSetting = parsedStorefrontSettings.storefrontShopSetting;

  return { storefrontShopSetting, location };
};

export default connect(mapStateToProps)(RentalItem);
